import { useMemo } from "react";
import { Button, Select, Switch, TextField, Typography } from "theme";
import { CrossMedium } from "theme/icons";

import { Box, FormControl, FormControlLabel, InputLabel, MenuItem, useTheme } from "@mui/material";
import { styled } from "@mui/system";

import { useFullClients } from "../../../api/hooks/useFullClients/useFullClients.ts";
import { useFilterSync } from "../../../utils/hooks/useFiltersSync.ts";

import Table from "./Table.tsx";

const StyledFiltersWrapper = styled(Box)(({ theme }) => ({
  borderBottomWidth: 1,
  borderBottomStyle: "solid",
  borderBottomColor: theme.palette.secondary.contrastText,
  display: "flex",
  alignItems: "center",
  columnGap: theme.spacing(5),
  padding: theme.spacing(1.5, 2.5),
}));

export enum SortingOptions {
  Id = "Id",
  Name = "Name",
  DiscountPercentage = "DiscountPercentage",
  Balance = "Balance",
  TotalBookingsCount = "TotalBookingsCount",
  TotalBookingHours = "TotalBookingHours",
  TotalClientIncome = "TotalClientIncome",
  TotalDeletedBookingsCount = "TotalDeletedBookingsCount",
}

const ClientsTable = () => {
  const { palette } = useTheme();

  const initialState = useMemo(
    () => ({
      quickSearch: "",
      sortBy: SortingOptions.Name,
      sortDirection: 'Asc',
      isInBlackList: false,
      page: 0,
      pageSize: 20,
    }),
    [],
  );

  const { updateFilters, filters, clearFilters } = useFilterSync({
    storageKey: "clientsList",
    initialState,
  });

  const { data, isFetching } = useFullClients({
    page: filters.page + 1,
    pageSize: filters.pageSize,
    search: filters.quickSearch,
    sortBy: filters.sortBy,
    sortDirection: filters.sortDirection
  });

  return (
    <Box>
      <StyledFiltersWrapper>
          <TextField
            name="search"
            value={filters.quickSearch}
            onChange={(e) => updateFilters({ quickSearch: e.target.value })}
            label="Пошук"
          />

        <FormControl fullWidth>
          <InputLabel id="hall" style={{
            color: palette.text.primary,
          }}>Фільтрація</InputLabel>
          <Select
            fullWidth
            MenuProps={{
              sx: {
                "& .MuiPaper-root": {
                  background: palette.primary.main,
                },
              },
            }}
            value={filters.sortBy}
            labelId="hall"
            onChange={(e) => updateFilters({
              sortBy: e.target.value as SortingOptions,
              page: 0
            })}
          >
            {Object.values(SortingOptions).map((el) => {
              return (
                <MenuItem key={el} value={el}>
                  {el}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="hall" style={{
            color: palette.text.primary,
          }}>Сортування</InputLabel>
          <Select
            fullWidth
            MenuProps={{
              sx: {
                "& .MuiPaper-root": {
                  background: palette.primary.main,
                },
              },
            }}
            value={filters.sortDirection}
            labelId="order"
            onChange={(e) => updateFilters({
              sortDirection: e.target.value as SortingOptions,
              page: 0
            })}
          >
            {['Asc', 'Desc'].map((el) => {
              return (
                <MenuItem key={el} value={el}>
                  {el === 'Asc' ? "По зростанню" : "По спаданню"}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={filters.isInBlackList}
                sx={{ m: 1 }}
                onChange={() => updateFilters({
                  isInBlackList: !filters.isInBlackList,
                })}
              />
            }
            label={
              <Typography variant="body1" weight="semiBold">
                Чорний список
              </Typography>
            }
          />
        </Box>

        <Button onClick={clearFilters} title="Очистити" look="normal">
          <CrossMedium />
        </Button>
      </StyledFiltersWrapper>

      <Box p={2.5}>
        <Table data={data} loading={isFetching} paginationModel={{
          page: filters.page,
          pageSize: filters.pageSize,
        }} setPaginationModel={value => {
          updateFilters({
            page: value.pageSize === filters.pageSize ? value.page : 0,
            pageSize: value.pageSize,
          });
        }} />
      </Box>
    </Box>
  );
};

export default ClientsTable;
